import { useMemo, useState } from "react";
import { Grid3X3, History, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRunTargetareMatrice } from "@/hooks/api/mutate/useRunTargetareMatrice";
import { useSalveazaTargetareMapare } from "@/hooks/api/mutate/useSalveazaTargetareMapare";
import { useSalveazaTargetareMapareCod } from "@/hooks/api/mutate/useSalveazaTargetareMapareCod";
import { useGetTargetareMapare } from "@/hooks/api/query/useGetTargetareMapare";
import { useGetTargetareStats } from "@/hooks/api/query/useGetTargetareStats";
import { useToast } from "@/hooks/use-toast";
import type {
  TargetareServiciuMapare,
  TargetareSugestieMapare,
} from "@/types/targetare";
import { coloanaMapare } from "@/utils/targetareConfig";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { jobActiv } from "@/utils/targetareJoburi";
import {
  definitieRaportMapare,
  randuriMapare,
} from "@/utils/targetareRaportMapare";
import {
  FirmeCriteriuDialog,
  type FirmeCriteriuDeschis,
} from "../components/FirmeCriteriuDialog";
import { HeroPagina } from "../components/HeroPagina";
import { IstoricModificari } from "../components/IstoricModificari";
import { celulaMapare } from "../components/mapare/CelulaMapare";
import { ServiciuMapareCard } from "../components/mapare/ServiciuMapareCard";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";

const TOATE = "toate";

export default function MapareTargetare() {
  const { toast } = useToast();
  const mapare = useGetTargetareMapare();
  const stats = useGetTargetareStats();
  const ruleazaMatrice = useRunTargetareMatrice();
  const salveaza = useSalveazaTargetareMapare();
  const salveazaCod = useSalveazaTargetareMapareCod();
  const [mod, setMod] = useState<"tabel" | "servicii">("tabel");
  const [sector, setSector] = useState(TOATE);
  const [cautare, setCautare] = useState("");
  const [firme, setFirme] = useState<FirmeCriteriuDeschis | null>(null);
  const [istoric, setIstoric] = useState(false);

  const servicii = useMemo(() => mapare.data?.servicii ?? [], [mapare.data]);
  const sectoareToate = useMemo(
    () => mapare.data?.sectoare ?? [],
    [mapare.data]
  );
  const sectoare = useMemo(
    () =>
      Array.from(
        new Set([
          ...servicii.map((s) => s.sector ?? "(fără sector)"),
          ...sectoareToate.map((s) => s.nume),
        ])
      ),
    [servicii, sectoareToate]
  );
  const filtrate = useMemo(() => {
    const q = cautare.trim().toLowerCase();
    return servicii.filter(
      (s) =>
        (sector === TOATE || (s.sector ?? "(fără sector)") === sector) &&
        (q === "" ||
          s.name.toLowerCase().includes(q) ||
          s.entity_id.includes(q) ||
          s.coduri.some((c) => c.cod.includes(q)))
    );
  }, [servicii, sector, cautare]);
  const grupate = useMemo(() => {
    const m = new Map<string, TargetareServiciuMapare[]>();
    filtrate.forEach((s) => {
      const k = s.sector ?? "(fără sector)";
      m.set(k, [...(m.get(k) ?? []), s]);
    });
    return Array.from(m.entries());
  }, [filtrate]);
  const randuriTabel = useMemo(
    () =>
      randuriMapare(
        filtrate,
        cautare.trim() === ""
          ? sectoareToate.filter((s) => sector === TOATE || s.nume === sector)
          : []
      ),
    [filtrate, sectoareToate, sector, cautare]
  );

  const sumar = useMemo(() => {
    const coduri = servicii.flatMap((s) => s.coduri);
    const active = coduri.filter((c) => c.activ);
    return {
      servicii: servicii.length,
      p2: active.filter((c) => c.versiune === 2 && !c.secundar).length,
      p3: active.filter((c) => c.versiune === 3 && !c.secundar).length,
      secundare: active.filter((c) => c.secundar).length,
      dezactivate: coduri.filter((c) => !c.activ).length,
      ambiguitatePeCod: active.filter((c) => c.ambiguitate).length,
      invalide: active.filter((c) => !c.valid).length,
      faraCod: servicii.filter((s) => !s.coduri.some((c) => c.activ)).length,
      sugestii: servicii.reduce((n, s) => n + s.sugestii.length, 0),
      partajate: active.filter((c) => c.alte_servicii.length > 0).length,
      sectoareFaraServicii: sectoareToate.filter((s) => s.servicii === 0)
        .length,
    };
  }, [servicii, sectoareToate]);

  const jobInCurs = jobActiv(stats.data?.joburi ?? []);

  const adaugaSugestie = (
    s: TargetareServiciuMapare,
    sg: TargetareSugestieMapare
  ) => {
    const coloana = coloanaMapare(sg.versiune, true);
    const existente = s.coduri
      .filter((c) => c.versiune === sg.versiune && c.secundar && c.activ)
      .map((c) => c.cod);
    salveaza.mutate(
      { entity_id: s.entity_id, coloana, coduri: [...existente, sg.cod] },
      {
        onSuccess: () =>
          toast({
            title: `${sg.cod} adăugat ca secundar`,
            description: `${s.name}, CAEN Rev. ${sg.versiune}. Rulează reîncadrarea ca firmele să reflecte schimbarea.`,
          }),
        onError: (e) =>
          toast({
            title: "Adăugarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  const celula = useMemo(
    () =>
      celulaMapare({
        inCurs: salveazaCod.isPending,
        onFirme: setFirme,
        onSalveaza: (patch) =>
          salveazaCod.mutate(patch, {
            onSuccess: (r) =>
              toast({
                title:
                  patch.activ === undefined
                    ? "Nota codului a fost salvată"
                    : r.data.activ
                      ? `${patch.cod} este din nou în mapare`
                      : `${patch.cod} a fost scos din mapare`,
                description:
                  patch.activ === undefined
                    ? "Ambiguitatea pe cod se aplică la următoarea reîncadrare."
                    : "Rulează reîncadrarea ca firmele să reflecte schimbarea.",
              }),
            onError: (e) =>
              toast({
                title: "Salvarea a eșuat",
                description: e.message,
                variant: "destructive",
              }),
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salveazaCod.isPending]
  );

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Maparea CAEN → servicii NIS2"
        descriere={
          <>
            Toate sectoarele și serviciile din nomenclatorul NIS2 (aceeași
            tabelă ca formularul de notificare), cu codurile CAEN principale și
            secundare în Rev. 2 și Rev. 3, descrierea fiecărui cod și numărul de
            firme care îl au ca activitate principală în bilanț sau ca
            activitate autorizată la ONRC (în scop / total). Fiecare cod se
            poate dezactiva (iese din mapare, dar rămâne în listă cu nota lui),
            primește o ambiguitate proprie care înlocuiește tipul serviciului la
            încadrare și o explicație a includerii. Notele inițiale vin din fișa
            de lucru DNSC (tip de corespondență Rev. 2 → Rev. 3 și context).
            Fiecare modificare rămâne în istoric.
            {mapare.data?.calculat_la &&
              ` Numărul de firme: matricea din ${formatDataOra(mapare.data.calculat_la)}.`}
          </>
        }
        isLoading={mapare.isLoading}
        tiles={[
          { eticheta: "Servicii", valoare: sumar.servicii },
          {
            eticheta: "Sectoare fără servicii",
            valoare: sumar.sectoareFaraServicii,
            detaliu: "identificate doar prin liste",
            ton: sumar.sectoareFaraServicii > 0 ? "atentie" : "bun",
          },
          { eticheta: "Coduri principale Rev. 2", valoare: sumar.p2 },
          { eticheta: "Coduri principale Rev. 3", valoare: sumar.p3 },
          { eticheta: "Coduri secundare", valoare: sumar.secundare },
          {
            eticheta: "Coduri dezactivate",
            valoare: sumar.dezactivate,
            detaliu: "scoase din mapare, păstrate cu notă",
            ton: sumar.dezactivate > 0 ? "atentie" : "bun",
          },
          {
            eticheta: "Ambiguitate setată pe cod",
            valoare: sumar.ambiguitatePeCod,
            detaliu: "înlocuiește tipul serviciului",
          },
          {
            eticheta: "Coduri invalide",
            valoare: sumar.invalide,
            ton: sumar.invalide > 0 ? "rau" : "bun",
            detaliu: "inexistente în nomenclatorul versiunii",
          },
          {
            eticheta: "Coduri partajate",
            valoare: sumar.partajate,
            detaliu: "același cod la mai multe servicii",
            ton: "atentie",
          },
          {
            eticheta: "Servicii fără cod CAEN",
            valoare: sumar.faraCod,
            detaliu: "identificate doar prin liste",
          },
          { eticheta: "Sugestii din liste", valoare: sumar.sugestii },
        ]}
        coloane={4}
        actiuni={
          <>
            <Button
              variant="outline"
              onClick={() =>
                ruleazaMatrice.mutate(undefined, {
                  onSuccess: (r) =>
                    toast({
                      title: "Recalculare programată",
                      description: `Job #${r.data.job_id} rulează în fundal (câteva minute).`,
                    }),
                  onError: (e) =>
                    toast({
                      title: "Recalcularea a eșuat",
                      description: e.message,
                      variant: "destructive",
                    }),
                })
              }
              disabled={ruleazaMatrice.isPending || !!jobInCurs}
            >
              {ruleazaMatrice.isPending || jobInCurs ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Grid3X3 className="mr-2 h-4 w-4" />
              )}
              Recalculează numărul de firme
            </Button>
            <Button variant="outline" onClick={() => setIstoric((v) => !v)}>
              <History className="mr-2 h-4 w-4" />
              Istoric ({mapare.data?.istoric.length ?? 0})
            </Button>
          </>
        }
      />

      {jobInCurs && (
        <p className="rounded-md border border-ds-blue-200 bg-ds-blue-50 p-3 text-sm text-ds-blue-700">
          Job #{jobInCurs.id} ({jobInCurs.tip}) rulează în fundal.
        </p>
      )}
      {mapare.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Maparea nu a putut fi încărcată: {mapare.error.message}
        </p>
      )}

      {istoric && (
        <IstoricModificari
          modificari={mapare.data?.istoric ?? []}
          numeCheie={(cheie) =>
            servicii.find((s) => s.entity_id === cheie)?.name ?? cheie
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          value={mod}
          onValueChange={(v) => setMod(v as "tabel" | "servicii")}
        >
          <TabsList>
            <TabsTrigger value="tabel">Tabel pe coduri</TabsTrigger>
            <TabsTrigger value="servicii">Fișe pe servicii</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={sector} onValueChange={setSector}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Sector" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOATE}>Toate sectoarele</SelectItem>
            {sectoare.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={cautare}
          onChange={(e) => setCautare(e.target.value)}
          placeholder="Caută serviciu, id sau cod CAEN"
          className="w-[280px]"
        />
        <span className="text-sm text-muted-foreground">
          {formatNumar(filtrate.length)} servicii
          {mod === "tabel" && ` · ${formatNumar(randuriTabel.length)} rânduri`}
        </span>
      </div>

      {mod === "servicii" ? (
        <div className="space-y-6">
          {grupate.map(([numeSector, lista]) => (
            <section key={numeSector} className="space-y-3">
              <h3 className="sticky top-0 z-10 bg-background py-1 text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
                {numeSector} · {lista.length} servicii
              </h3>
              {lista.map((s) => (
                <ServiciuMapareCard
                  key={s.entity_id}
                  serviciu={s}
                  onFirme={setFirme}
                  onAdaugaSugestie={adaugaSugestie}
                  adaugaInCurs={salveaza.isPending}
                />
              ))}
            </section>
          ))}
          {!mapare.isLoading && grupate.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Niciun serviciu nu corespunde filtrului.
            </p>
          )}
        </div>
      ) : (
        <RaportInteractiv
          definitie={definitieRaportMapare}
          randuri={randuriTabel}
          isLoading={mapare.isLoading}
          celula={celula}
        />
      )}

      <FirmeCriteriuDialog deschis={firme} onClose={() => setFirme(null)} />
    </div>
  );
}
